import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User2, Plus } from "lucide-react";
import Navbar from "../components/Navbar";
import SupportChip from "../components/SupportChip";

/* ----------------------------- Types & mock ----------------------------- */
type Account = {
  id: string;
  bank: string;
  holderMeta?: string; // e.g. "22, Female"
  accountNumber: string; // unmasked
  code?: string | number;
  branchName?: string;
};

const initialAccounts: Account[] = [
  {
    id: "a1",
    bank: "CHASE",
    holderMeta: undefined,
    accountNumber: "1234566789",
    code: 8,
    branchName: "Branch Name",
  },
  {
    id: "a2",
    bank: "CHASE",
    holderMeta: undefined,
    accountNumber: "1234566789",
    code: 8,
    branchName: "Branch Name",
  },
];

/* ------------------------------- Helpers ------------------------------- */
const maskAccount = (num: string) => `*****${num.slice(-6)}`;

/* ------------------------------ Components ----------------------------- */

function DefaultAccountCard({ account }: { account: Account }) {
  return (
    <div className="bg-white border border-[#DEDEDE]">
      {/* header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-10 h-10 border border-[#DEDEDE] grid place-items-center">
          <User2 className="w-5 h-5 text-[#2D2D2D]" />
        </div>
        <div>
          <div className="text-[14px] font-extrabold text-[#2D2D2D]">
            {account.bank || "Bank Name"}
          </div>
          {account.holderMeta && (
            <div className="text-[12px] text-[#8A8A8A]">{account.holderMeta}</div>
          )}
        </div>
      </div>

      <div className="h-px bg-[#EAEAEA]" />

      {/* details */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between py-2">
          <div className="text-[12px] text-[#8A8A8A]">Account Number</div>
          <div className="text-[12px] font-extrabold text-[#2D2D2D]">
            {maskAccount(account.accountNumber)}
          </div>
        </div>

        <div className="flex items-center justify-between py-2">
          <div className="text-[12px] text-[#8A8A8A]">Code</div>
          <div className="text-[12px] font-extrabold text-[#2D2D2D]">
            {account.code ?? "-"}
          </div>
        </div>

        <div className="flex items-center justify-between py-2">
          <div className="text-[12px] text-[#8A8A8A]">Branch Name</div>
          <div className="text-[12px] font-extrabold text-[#2D2D2D]">
            {account.branchName ?? "-"}
          </div>
        </div>
      </div>
    </div>
  );
}

function AccountRow({
  account,
  isDefault,
  onMakeDefault,
  onDelete,
}: {
  account: Account;
  isDefault: boolean;
  onMakeDefault: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="bg-white border border-[#DEDEDE] px-4 py-3">
      <div className="text-[12px] font-extrabold text-[#2D2D2D] uppercase">
        {account.bank}
      </div>

      <div className="mt-2 text-[12px] text-[#8A8A8A]">
        Account Number:{" "}
        <span className="text-[#2D2D2D]">{account.accountNumber}</span>
      </div>

      <div className="mt-3 flex items-center gap-6">
        <button
          className={[
            "text-[12px] font-extrabold",
            isDefault ? "text-[#B8B8B8] cursor-default" : "text-[#D62422]",
          ].join(" ")}
          onClick={isDefault ? undefined : onMakeDefault}
          aria-disabled={isDefault}
        >
          MAKE DEFAULT
        </button>

        <button
          className="text-[12px] font-extrabold text-[#D62422]"
          onClick={onDelete}
        >
          DELETE
        </button>
      </div>
    </div>
  );
}

/* --------------------------------- Screen -------------------------------- */
const TrainerPaymentInfo: React.FC = () => {
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>(initialAccounts);
  const [defaultId, setDefaultId] = useState<string>(initialAccounts[0].id);

  const defaultAccount = useMemo(
    () => accounts.find((a) => a.id === defaultId) ?? accounts[0],
    [accounts, defaultId]
  );

  const addAccount = () => {
    const n = accounts.length + 1;
    const next: Account = {
      id: `a${Date.now()}`,
      bank: "CHASE",
      accountNumber: `12345667${80 + n}`,
      code: 8,
      branchName: "Branch Name",
    };
    setAccounts((prev) => [...prev, next]);
  };

  const deleteAccount = (id: string) => {
    setAccounts((prev) => prev.filter((a) => a.id !== id));
    if (id === defaultId) {
      // move default to the first remaining, if any
      setTimeout(() => {
        setDefaultId((cur) => {
          const list = accounts.filter((a) => a.id !== id);
          return list[0]?.id ?? cur;
        });
      }, 0);
    }
  };

  return (
    <div
      className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div className="mx-auto w-full min-h-[100dvh]">
        {/* Dark hero */}
        <header className="relative w-full bg-[linear-gradient(157.07deg,#3a3a3a_0%,#252525_81.65%)] text-white pb-6">
          <Navbar
            onBack={() => navigate("/trainer/profile")}
            right={<SupportChip />}
          />

          <div className="px-5 pt-2">
            <h1 className="text-[18px] font-extrabold">Payment Info</h1>

            {/* default card */}
            <div className="mt-4">
              {defaultAccount && <DefaultAccountCard account={defaultAccount} />}
            </div>

            {/* helper text */}
            <div className="mt-4 text-center text-[12px] text-white/70">
              Your earnings will be sent to your bank account within 7 days.
            </div>

            {/* add account */}
            <button
              className="mt-5 inline-flex items-center gap-2 text-white font-extrabold"
              onClick={addAccount}
            >
              <Plus className="w-4 h-4" />
              ADD NEW ACCOUNT
            </button>
          </div>
        </header>

        {/* Accounts list */}
        <main className="px-5 py-6">
          <div className="text-[12px] text-[#8A8A8A] uppercase tracking-wide mb-3">
            // ACCOUNTS
          </div>

          <div className="space-y-3">
            {accounts.map((acc) => (
              <AccountRow
                key={acc.id}
                account={acc}
                isDefault={acc.id === defaultId}
                onMakeDefault={() => setDefaultId(acc.id)}
                onDelete={() => deleteAccount(acc.id)}
              />
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TrainerPaymentInfo;
